import { isAxiosError } from 'axios'
import { isFunction, isString } from 'lodash-es'

import { captureAndLogError } from '@/lib/error'

import { AppError, AppErrorOptions, isSpecificError } from './AppError'
import { ErrorCode } from './ErrorCode'
import { ApiErrorCode, DetailedApiError, DetailedApiErrorZ } from './ErrorResponse'

export type ApiErrorMap<R> = Partial<Record<ApiErrorCode | '_', ((e: DetailedApiError) => R) | ErrorCode>>

export class GGClientError extends AppError {
	static is(error?: unknown, code?: ErrorCode | ErrorCode[]): error is GGClientError {
		return isSpecificError(GGClientError, error, code)
	}

	static matchApiError<R = never>(errorMap: ApiErrorMap<R>) {
		return function apiErrorMatcher(error: DetailedApiError): R {
			const valueMapper = errorMap[error.code] ?? errorMap['_']
			if (isFunction(valueMapper)) {
				return valueMapper(error)
			}
			throw new GGClientError({
				code: isString(valueMapper) ? valueMapper : ErrorCode.not_mapped,
				serverCode: error.code,
				message: error.message,
				reason: error.reason,
				extra: error.extra,
			})
		}
	}

	static matchAxiosError<R = never>(errorMap: ApiErrorMap<R>) {
		return function axiosErrorMatcher(error: unknown): R {
			if (!isAxiosError(error)) {
				throw error
			}
			if (error.response) {
				const result = DetailedApiErrorZ.safeParse(error.response.data)
				if (result.success) {
					return GGClientError.matchApiError(errorMap)(result.data)
				} else if (error.response.status >= 500) {
					throw new GGClientError({
						code: ErrorCode.server_error,
						message: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because of 500 response',
						cause: error,
					})
				} else if (error.response.status === 429) {
					throw new GGClientError({
						code: ErrorCode.too_many_requests,
						message: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because of 429 response',
						cause: error,
					})
				} else if (error.response.status === 403) {
					throw new GGClientError({
						code: ErrorCode.not_authorized,
						message: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because of 403 response',
						cause: error,
					})
				} else if (error.response.status === 401) {
					throw new GGClientError({
						code: ErrorCode.not_authenticated,
						message: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because of 401 response',
						cause: error,
					})
				} else if (error.response.status === 400) {
					throw new GGClientError({
						code: ErrorCode.server_error,
						message:
							typeof error.response.data === 'string'
								? error.response.data
								: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because of 400 response',
						cause: error,
					})
				} else {
					captureAndLogError(result.error, {
						level: 'warning',
						extra: {
							method: error.config?.method,
							source: 'response_validation',
						},
					})
					throw new GGClientError({
						code: ErrorCode.invalid_response,
						message: `${error.response.status} ${error.response.statusText}`,
						reason: 'Auto mapped because error response format is invalid',
						cause: new AggregateError([result.error, error]),
					})
				}
			} else if (error.request) {
				throw new GGClientError({
					code: ErrorCode.connection_error,
					message: 'Connection failed and no response has been received',
					reason: 'Auto mapped because of no response',
					cause: error,
				})
			} else {
				throw new GGClientError({
					code: ErrorCode.misconfiguration,
					message: 'The client failed to initiate the request',
					reason: 'Auto mapped because of no request',
					cause: error,
				})
			}
		}
	}

	static matchNotMappedError<R = never>(
		errorMap: Partial<Record<Exclude<ApiErrorCode, ErrorCode> | '_', ((e: GGClientError) => R) | ErrorCode>> &
			Partial<Record<ErrorCode, ((e: GGClientError) => R) | ErrorCode | true>>,
	) {
		return function unknownClientErrorMatcher(error: unknown): R {
			if (!GGClientError.is(error, ErrorCode.not_mapped)) {
				throw error
			}
			const valueMapper = errorMap[error.serverCode as ApiErrorCode | ErrorCode] ?? errorMap['_']
			if (isFunction(valueMapper)) {
				return valueMapper(error)
			}
			if (isString(valueMapper) && valueMapper !== error.code) {
				throw new GGClientError({
					...error,
					code: valueMapper,
					reason: 'Remapped',
					cause: error,
				})
			}
			if (valueMapper === true) {
				throw new GGClientError({
					...error,
					code: error.serverCode as ErrorCode,
					reason: 'Remapped',
					cause: error,
				})
			}
			throw error
		}
	}

	serverCode?: string
	reason?: string
	extra?: Record<string, unknown>

	constructor(options: AppErrorOptions & { serverCode?: string; reason?: string; extra?: Record<string, unknown> }) {
		super(options)
		this.serverCode = options.serverCode
		this.reason = options.reason
		this.extra = options.extra
	}
}
